<template>
  <div
    class="AuthRegister"
    data-cy-id="register-stage-diversity"
  >
    <section>
      <h1>Diversity</h1>
      <p class="hint">
        {{ $lang.diversityQuestions.explainer }}
        <br>
        <a
          target="_blank"
          href="/blog/privacy-policy/"
        >  {{ $lang.diversityQuestions.privacyPolicy }}</a>
      </p>
    </section>
    <section class="no-border">
      <form
        ref="registration"
        class="Form"
      >
        <div class="Form-field">
          <label
            class="Form-label font-heavy"
            for="ethnicity"
          >What is your ethnicity?</label>
          <select
            id="ethnicity"
            v-model="fields.ethnicity"
            data-cy-id="register-ethnicity"
            required
          >
            <optgroup
              v-for="group in ethnicities"
              :key="group.id"
              :label="group.text"
            >
              <option
                v-for="ethnicity in group.ethnicities"
                :key="ethnicity.id"
                :value="ethnicity.value"
              >
                {{ ethnicity.text }}
              </option>
            </optgroup>
          </select>
          <error-messages
            :errors="[validateStatus({ field: v$.fields.ethnicity, serverKey: 'ethnicity', label: 'Ethnicity' })][0]['errors']"
            :manual-validate="validateAll"
          />
        </div>
        <div class="Form-field">
          <label
            for="schoolmeal+0"
            class="Form-label font-heavy "
          >Are / were you eligible for free school meals?</label>
          <div
            v-for="(schoolMeals, index) in freeSchoolMeals"
            :key="schoolMeals.text"
          >
            <rmp-radio
              :id="`schoolmeal+${index}`"
              v-model="fields.free_school_meals"
              data-cy-id="register-free-school-meals"
              :label="schoolMeals.text"
              :value="schoolMeals.value"
              :name="`schoolmeal+${schoolMeals.id}`"
            />
          </div>
          <error-messages
            :errors="[validateStatus({ field: v$.fields.free_school_meals, serverKey: 'freeSchoolMeals', label: 'Free School Meals' })][0]['errors']"
            :manual-validate="validateAll"
          />
        </div>
        <div
          class="Form-field"
        >
          <div
            v-if="!isModalRegistration"
            class="ParentProfesion-modal-variant"
          >
            <label
              for="profession+0"
              class="Form-label font-heavy"
            >What was the occupation of your main household earner when you were aged 14? (if you're younger than 14, please leave blank)</label>
            <div
              v-if="professionSelection"
              class="Tag Tag--blue Tag--small"
            >
              {{ professionSelection }}
            </div>
            <profession-selection-modal
              data-cy-id="register-parents-profession"
              :parent-professions="parentProfessions"
              @parent-profession-selection="handleProfessionSelection"
            />
          </div>
          <div
            v-else
          >
            <label
              for="profession+0"
              class="Form-label font-heavy"
            >What was the occupation of your main household earner when you were aged 14? (if you're younger than 14, please leave blank)</label>

            <parent-profession-radio-variant
              :parent-professions="parentProfessions"
              @parent-profession-selection="handleProfessionSelection"
            />
          </div>
          <error-messages
            :errors="[validateStatus({ field: v$.fields.parent_profession, serverKey: 'parentProfession', label: `Parent's Profession` })][0]['errors']"
            :manual-validate="validateAll"
          />
        </div>
        <button
          :class="['Button Button--primary Button--long', {'spinner' : ui.loading}]"
          data-cy-id="register-next"
          @click.prevent="handleSubmit"
        >
          Continue
        </button>
      </form>
    </section>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import FormAutocomplete from '@/components/form/autocomplete-select';
import errorMessages from '@/components/form/partials/error-message.vue';
import FormCheckbox from '@/components/form/checkbox';
import rmpRadio from '@/components/form/radio-button';
import ParentProfessionRadioVariant from '@/components/incremental-data/partials/parent-profession-radio-variant.vue';
import ProfessionSelectionModal from '@/components/incremental-data/modals/profession-selection-modal.vue';
import RegistrationStagesMixin from './registration-stages-mixin';

export default {

  components: {
    FormCheckbox,
    FormAutocomplete,
    errorMessages,
    ProfessionSelectionModal,
    rmpRadio,
    ParentProfessionRadioVariant,
  },

  mixins: [RegistrationStagesMixin],

  props: {
    isModalRegistration: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      toggleHint: [],
      fields: {
        ethnicity: null,
        free_school_meals: null,
        parent_profession: null,
      },
      serverValidationErrors: [],
    };
  },
  validations() {
    const rules = {
      fields: {
        ethnicity: {
          required,
        },
        parent_profession: {
          required,
        },
        free_school_meals: {
          required,
        },
      },

    };
    return rules;
  },
  computed: {
    ...mapState('entities', {
      ethnicities: (state) => state.ethnicities,
      parentProfessions: (state) => state.parentProfessions,
      freeSchoolMeals: (state) => state.freeSchoolMeals,
    }),
    updatePayload() {
      return {
        ethnicity: this.fields.ethnicity,
        parent_profession: this.fields.parent_profession,
        free_school_meals: this.fields.free_school_meals,
      };
    },
    professionSelection() {
      const profession = this.parentProfessions.find((item) => item.value === this.fields.parent_profession);
      return profession ? profession.text : null;
    },
  },
  mounted() {
    Promise.all([
      this.getEthnicities(),
      this.getParentProfessions(),
      this.getFreeSchoolMeals(),
    ])
      .then(() => {
      })
      .catch((error) => {
        this.$bus.$emit('bus:base-alert', {
          message: this.$lang.alert.error.heading,
          type: 'Error',
        });
        console.log(error.response);
      });
  },
  methods: {
    ...mapActions('entities', {
      getEthnicities: 'getEthnicities',
      getFreeSchoolMeals: 'getFreeSchoolMeals',
      getParentProfessions: 'getParentProfessions',
    }),

    handleProfessionSelection(item) {
      this.fields = { ...this.fields, parent_profession: item };
      this.v$.fields.parent_profession.$touch();
    },
    handleSubmit() {
      this.updateUser();

      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../scss/components/diversity-questions/questions.scss";
</style>
